import { Loader } from "lucide-react";
import { FC, useCallback } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useResequenceSKUMutation } from "@/data/mutations";
import { useGetOrganizationMembershipsQuery } from "@/data/queries";
import { useDialogState } from "@/hooks/use-dialog-state";

type Props = {
  skuId: string;
};

export const ResequenceSKUDialog: FC<Props> = ({ skuId }) => {
  const { dialog, closeDialog } = useDialogState();
  const resequenceSKUMutation = useResequenceSKUMutation();
  const { data } = useGetOrganizationMembershipsQuery();

  const handleSubmit = useCallback(() => {
    resequenceSKUMutation.mutate({ skuId });
  }, [resequenceSKUMutation, skuId]);

  if (!data) {
    return null;
  }

  return (
    <AlertDialog open={dialog?.name === "resequence-sku"}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resequence Tags in SKU {skuId}</AlertDialogTitle>
          <AlertDialogDescription>
            Reorder serial numbers for all tags in this SKU by their programming
            time starting from 1. This will not affect the tag IDs.
            <br />
            <br />
            Use this only if tags have been transferred or removed and that
            there are gaps in the serial numbers that need to be filled.
            <br />
            <br />
            This action cannot be undone and may affect all tags in this SKU.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={resequenceSKUMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            variant="destructive"
            disabled={resequenceSKUMutation.isPending}
          >
            {resequenceSKUMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : null}
            Resequence
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
